import { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Upload, Trash2, Loader2, GripVertical } from "lucide-react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

function SortableCard({ item, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 10 : "auto",
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`gallery-card-${item.id}`}
      className={`bg-white border rounded-sm overflow-hidden ${isDragging ? "border-clay shadow-lg" : "border-sand-300"}`}
    >
      <div className="aspect-[4/3] relative">
        <img src={fileUrl(item.image_path)} alt={item.title} className="w-full h-full object-cover" />
        <button
          type="button"
          {...attributes}
          {...listeners}
          data-testid={`gallery-drag-handle-${item.id}`}
          aria-label="Zum Verschieben ziehen"
          className="absolute top-2 left-2 p-2 bg-stone-900/70 text-sand-100 rounded-sm cursor-grab active:cursor-grabbing touch-none hover:bg-clay transition-colors"
        >
          <GripVertical className="w-4 h-4" />
        </button>
      </div>
      <div className="p-4 flex justify-between items-center">
        <div className="min-w-0">
          <p className="font-medium text-stone-900 truncate">{item.title || "Ohne Titel"}</p>
          {item.description && <p className="text-sm text-stone-600 truncate">{item.description}</p>}
        </div>
        <Button size="sm" variant="ghost" onClick={() => onDelete(item.id)} data-testid={`gallery-delete-${item.id}`} className="text-stone-700 hover:text-clay hover:bg-sand-200">
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

export default function GalleryManager() {
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 8 } })
  );

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/gallery");
    setItems(data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Bitte ein Bild auswählen");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: up } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post("/admin/gallery", { title, description, image_path: up.path, sort_order: items.length });
      toast.success("Bild hochgeladen");
      setTitle(""); setDescription(""); setFile(null);
      document.getElementById("gallery-file-input").value = "";
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload fehlgeschlagen");
    } finally { setUploading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Wirklich löschen?")) return;
    await api.delete(`/admin/gallery/${id}`);
    toast.success("Gelöscht");
    load();
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((it) => it.id === active.id);
    const newIndex = items.findIndex((it) => it.id === over.id);
    const reordered = arrayMove(items, oldIndex, newIndex);
    setItems(reordered);
    try {
      await api.put("/admin/gallery/reorder", { ids: reordered.map((it) => it.id) });
      toast.success("Reihenfolge gespeichert");
    } catch {
      toast.error("Reihenfolge konnte nicht gespeichert werden");
      load();
    }
  };

  return (
    <div data-testid="gallery-manager" className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <form onSubmit={handleUpload} className="lg:col-span-1 bg-white border border-sand-300 p-6 rounded-sm space-y-4 h-fit">
        <h3 className="font-display text-2xl text-stone-900">Neues Bild hochladen</h3>
        <div>
          <Label className="mb-2 block">Titel</Label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="gallery-title-input" />
        </div>
        <div>
          <Label className="mb-2 block">Beschreibung</Label>
          <Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="gallery-desc-input" />
        </div>
        <div>
          <Label className="mb-2 block">Bild *</Label>
          <Input id="gallery-file-input" type="file" accept="image/*" onChange={(e) => setFile(e.target.files[0])} data-testid="gallery-file-input" required />
        </div>
        <Button type="submit" disabled={uploading} data-testid="gallery-upload-submit" className="w-full bg-stone-900 hover:bg-clay text-sand-100 rounded-sm">
          {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Lädt hoch…</> : <><Upload className="w-4 h-4 mr-2" /> Hochladen</>}
        </Button>
      </form>

      <div className="lg:col-span-2">
        <div className="flex items-baseline justify-between mb-4 gap-4">
          <h3 className="font-display text-2xl text-stone-900">Galerie ({items.length})</h3>
          {items.length > 1 && (
            <p className="text-xs text-stone-600" data-testid="gallery-dnd-hint">
              <GripVertical className="w-3 h-3 inline -mt-0.5" /> Ziehen Sie die Bilder am Griff, um die Reihenfolge zu ändern.
            </p>
          )}
        </div>
        {loading ? <p className="text-stone-600">Laden…</p> : items.length === 0 ? (
          <p className="text-stone-600 italic" data-testid="gallery-empty">Noch keine Bilder. Laden Sie das erste Bild hoch.</p>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={items.map((it) => it.id)} strategy={rectSortingStrategy}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {items.map((it) => (
                  <SortableCard key={it.id} item={it} onDelete={handleDelete} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>
    </div>
  );
}
